import React from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Field, FieldLabel, FieldError } from '@tectonics/components/ui/field';
import { Input, inputVariants } from '@tectonics/components/ui/input';
import { PeruCVVTooltip } from '@tectonics/look-and-feel/volcanes/pages/page-opened-payment/payment-method/card-form/peruCvvTooltip';
import { useFormSecurityCodeField } from './hooks/use-form-security-code-field';
import { useSecurityCodeHelp } from './hooks/use-security-code-help';
import { FieldCardSecurityCodeTooltip } from './field-card-security-code-tooltip';
import { LEGACY_COPY } from './legacy-copy';
import type { LegacyCopyNamespace } from './legacy-copy';
import { VariantProps } from 'class-variance-authority';

interface FieldCardSecurityCodeProps {
  readonly legacyNamespace?: LegacyCopyNamespace;
  readonly lookAndFeel?: VariantProps<typeof inputVariants>['lookAndFeel'];
}

function FieldCardSecurityCode({ legacyNamespace = 'volcanes', lookAndFeel = 'volcanes' }: FieldCardSecurityCodeProps): ReactNode {
  const { t } = useTranslation('opened-page', { keyPrefix: 'volcanes.form.cardSecurityCode' });
  const { t: tLegacy } = useTranslation(legacyNamespace);
  const { labelKey } = LEGACY_COPY[legacyNamespace];
  const {
    inputId,
    cardDigits,
    cardBrand,
    isDemoMerchant,
    showPeruTooltip,
    hasError,
    errorMessage,
    submitting,
    value,
    handleChange,
    inputRef,
  } = useFormSecurityCodeField(legacyNamespace);
  const { helpOpen, setHelpOpen, openHelpOnFirstFocus } = useSecurityCodeHelp();

  const label = labelKey == null ? t('label') : tLegacy(labelKey);
  const placeholder = cardBrand == null
    ? t('placeholder')
    : '0'.repeat(cardDigits);

  return (
    <Field data-invalid={hasError} data-test-name="card-security-code" lookAndFeel={lookAndFeel}>
      <div className="flex items-center justify-between pb-px">
        <div className="flex items-center gap-2">
          <FieldLabel htmlFor={inputId} lookAndFeel={lookAndFeel}>{label}</FieldLabel>
          {showPeruTooltip && (
            <span className="relative inline-block size-5 -mb-2">
              <PeruCVVTooltip />
            </span>
          )}
        </div>
        {isDemoMerchant && <FieldCardSecurityCodeTooltip open={helpOpen} onOpenChange={setHelpOpen} inputRef={inputRef} />}
      </div>
      <Input
        ref={inputRef}
        id={inputId}
        name="card-security-code"
        type="tel"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onFocus={isDemoMerchant ? openHelpOnFirstFocus : undefined}
        readOnly={submitting}
        aria-invalid={hasError}
        data-error={hasError ? 'error' : null}
        data-secret
        lookAndFeel={lookAndFeel}
      />
      {hasError && (
        <FieldError lookAndFeel={lookAndFeel}>{errorMessage}</FieldError>
      )}
    </Field>
  );
}

export { FieldCardSecurityCode };
